import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { PageRenderer } from '../components/site/PageRenderer';
import { Seo } from '../components/site/Seo';
import { useSiteContent } from '../context/SiteContentContext';
import { useContentfulCollection } from '../hooks/useContentfulCollection';

const richTextOptions = {
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
    [MARKS.CODE]: (text) => <code>{text}</code>,
    [MARKS.STRIKETHROUGH]: (text) => <s>{text}</s>,
    [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
    [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
    [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
    [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
    [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
    [BLOCKS.HEADING_5]: (node, children) => <h5>{children}</h5>,
    [BLOCKS.HEADING_6]: (node, children) => <h6>{children}</h6>,
    [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
    [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
    [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
    [BLOCKS.QUOTE]: (node, children) => <blockquote>{children}</blockquote>,
    [BLOCKS.HR]: () => <hr />,
    [BLOCKS.TABLE]: (node, children) => (
      <div className='rich-text-table-wrapper'>
        <table><tbody>{children}</tbody></table>
      </div>
    ),
    [BLOCKS.TABLE_ROW]: (node, children) => <tr>{children}</tr>,
    [BLOCKS.TABLE_HEADER_CELL]: (node, children) => <th>{children}</th>,
    [BLOCKS.TABLE_CELL]: (node, children) => <td>{children}</td>,
    [INLINES.HYPERLINK]: (node, children) => (
      <a href={node.data.uri} target='_blank' rel='noopener noreferrer'>
        {children}
      </a>
    ),
  },
};

export function PrivacyPolicyPage() {
  const { siteContent, contentStatus } = useSiteContent();
  const page = siteContent.pages.privacyPolicy;
  const { items, isLoading } = useContentfulCollection('privacyPolicyContent', { pageSize: 1, immediate: true });
  const privacyPolicyContent = items[0] || siteContent.collections.privacyPolicyContent;

  if (privacyPolicyContent?.richText) {
    const formattedDate = privacyPolicyContent.date
      ? new Date(privacyPolicyContent.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null;

    return (
      <>
        <Seo page={page} />
        <article className='detail-shell privacy-policy-shell'>
          <p className='section-label'>Legal{formattedDate ? ` · ${formattedDate}` : ''}</p>
          <h1 className='detail-title'>Privacy policy</h1>
          <div className='detail-body markdown-body rich-text-body'>
            {documentToReactComponents(privacyPolicyContent.richText, richTextOptions)}
          </div>
        </article>
      </>
    );
  }

  if (isLoading && !privacyPolicyContent) {
    return (
      <>
        <Seo page={page} />
        <div className='page-shell loading-shell'>Loading privacy policy…</div>
      </>
    );
  }

  return (
    <>
      <Seo page={page} />
      <PageRenderer page={page} />
    </>
  );
}
